// Build-time-imported video model facts for the /models/:slug pages.
//
// The canonical source is website/content/video-models.json (git-tracked, committed content),
// beside image-models.json and for the same reasons (see imageModels.ts). AI Studio's model detail
// imports THIS module through the client's @website alias, so the picker and the pages share the
// facts, the wording and the formatting. A .NET test pins the file to the server catalog.

// Relative, not the @content alias: vite.config.ts imports this module before the alias exists.
import raw from '../../content/video-models.json'
import { TIER_ORDER, relatedByTier, type ModelTier } from './modelTiers'

export type VideoModelTier = ModelTier
export type VideoModelJob = 'words' | 'photo' | 'frames'

export interface VideoModelQuality {
  /** The server's quality key (matches the pricing feed's `quality`). */
  quality: string
  label?: string
  tier: VideoModelTier
  /** A line for this quality alone, shown in the picker instead of the model's `goodFor`. */
  goodFor?: string
  /** [min, max] seconds measured to generate one clip. */
  seconds: [number, number]
}

export interface VideoModelSample {
  /** The clip, a site-root path. */
  src: string
  /** Still frame shown until the clip is pressed. */
  poster: string
  shape: 'wide' | 'tall'
  alt: string
  prompt?: string
}

export interface VideoModel {
  /** Every catalog id this page covers (Wan 2.2 Fast has one for words and one for a photo). */
  modelIds: string[]
  slug: string
  name: string
  goodFor: string
  summary: string
  strengths: string[]
  limits: string[]
  jobs: VideoModelJob[]
  /** The clip lengths, in seconds, the model offers. */
  lengths: number[]
  sound: boolean
  qualities: VideoModelQuality[]
  samples: VideoModelSample[]
}

const JOB_WORDS: Record<VideoModelJob, string> = {
  words: 'Words',
  photo: 'A photo',
  frames: 'A first and last frame',
}

/** What a model takes, as one sentence: "Words, a photo, or a first and last frame". */
export function jobsText(jobs: VideoModelJob[]): string {
  const parts = jobs.map((j, i) => (i === 0 ? JOB_WORDS[j] : JOB_WORDS[j].charAt(0).toLowerCase() + JOB_WORDS[j].slice(1)))
  if (parts.length < 2) return parts.join('')
  return `${parts.slice(0, -1).join(', ')}${parts.length > 2 ? ',' : ''} or ${parts[parts.length - 1]}`
}

/** The per-job words, for callers that list them. */
export const JOBS_TEXT = JOB_WORDS

export const SOUND_TEXT = "Comes with its own sound. It stays silent under your song unless you turn the clip's sound on."

/** "5 seconds", "5 or 10 seconds", "4 to 10 seconds" (a run of consecutive lengths), else "4, 6 or 10 seconds". */
export function lengthsText(lengths: number[]): string {
  const l = [...lengths].sort((a, b) => a - b)
  if (l.length === 0) return ''
  if (l.length === 1) return `${l[0]} seconds`
  if (l.length > 2 && l.every((n, i) => i === 0 || n === l[i - 1] + 1)) return `${l[0]} to ${l[l.length - 1]} seconds`
  return `${l.slice(0, -1).join(', ')} or ${l[l.length - 1]} seconds`
}

/** Minutes to the nearest half, spelled "2", "3½", "7½". */
function minutesText(seconds: number): string {
  const halves = Math.round(seconds / 30)
  const whole = Math.floor(halves / 2)
  return `${whole === 0 ? '' : whole}${halves % 2 ? '½' : ''}`
}

/** How long a clip takes: "about 23 seconds", "about 23–27 seconds"; from 90 s in minutes, "about 2–3½ minutes". */
export function speedText(q: VideoModelQuality): string {
  const [lo, hi] = q.seconds
  if (hi >= 90) {
    const a = minutesText(lo)
    const b = minutesText(hi)
    return a === b ? `about ${a} minutes` : `about ${a}–${b} minutes`
  }
  return hi - lo > 3 ? `about ${lo}–${hi} seconds` : `about ${lo} seconds`
}

export { TIER_ORDER }

export const videoModels: VideoModel[] = (raw as unknown as { models: VideoModel[] }).models

export function videoModelBySlug(slug: string): VideoModel | undefined {
  return videoModels.find((m) => m.slug === slug)
}

/** The page covering a catalog id, matched ignoring case. */
export function videoModelByModelId(modelId: string): VideoModel | undefined {
  const id = modelId.toLowerCase()
  return videoModels.find((m) => m.modelIds.some((x) => x.toLowerCase() === id))
}

/** Other video models sharing a tier with this one, ordered by tier then file order. */
export function otherVideoModelsByTier(model: VideoModel): { tier: VideoModelTier; models: VideoModel[] }[] {
  return relatedByTier(model, videoModels)
}
